/**
 * Test Metadata Extraction
 *
 * Functions for extracting metadata from traces including state paths,
 * fingerprints, terminal IDs, and other test metadata.
 */

import type { MachineTrace } from "../trace-inspector";
import { detectOcrOutcome, extractConsumerName } from "./context-extraction";
import type { TestMetadata } from "./types";
import { getStateIdFromUrl } from "./utils";

// ============================================================================
// State Path Extraction
// ============================================================================

/**
 * Extract the unique sequence of states visited during the trace
 * Deduplicates consecutive same states (from multiple events in same state)
 */
export function extractStatePath(trace: MachineTrace): string[] {
  const states: string[] = [trace.initialState];

  for (const event of trace.events) {
    if (event.toState && event.toState !== states[states.length - 1]) {
      states.push(event.toState);
    }
  }

  return states;
}

// ============================================================================
// Fingerprint Generation
// ============================================================================

/**
 * Generate a short fingerprint from the state path
 * Uses a simple hash for brevity while maintaining uniqueness
 */
export function generateFingerprint(statePath: string[]): string {
  const pathString = statePath.join("→");
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < pathString.length; i++) {
    hash = (hash * 33) ^ pathString.charCodeAt(i);
  }
  // Convert to base36 for shorter string, take first 8 chars
  return Math.abs(hash).toString(36).slice(0, 8);
}

// ============================================================================
// Metadata Extraction
// ============================================================================

/**
 * Extract complete test metadata from a trace
 */
export function extractMetadata(trace: MachineTrace): TestMetadata {
  const machineEvents = trace.events.filter(
    (e) => e.inspectionType === "@xstate.event",
  );
  const domEventCount = machineEvents.reduce(
    (acc, e) => acc + (e.uiHint?.domEvents?.length || 0),
    0,
  );

  // Find final state
  const lastSnapshot = [...trace.events]
    .reverse()
    .find((e) => e.inspectionType === "@xstate.snapshot");
  const finalState = lastSnapshot?.toState || trace.initialState;

  // Calculate duration
  const lastEvent = trace.events[trace.events.length - 1];
  const durationMs = lastEvent?.relativeTime || 0;
  const duration =
    durationMs < 1000
      ? `${durationMs}ms`
      : `${(durationMs / 1000).toFixed(2)}s`;

  // Extract unique state path (sequence of states visited)
  // This is more stable than events for deduplication
  const statePath = extractStatePath(trace);
  // Use only last 4 states for fingerprint - captures the "error neighborhood"
  // without being too specific about the full journey
  const fingerprintPath = statePath.slice(-4);
  const statePathFingerprint = generateFingerprint(fingerprintPath);

  // Capture Stripe Terminal reader ID from localStorage (for POS payment testing)
  const terminalId =
    typeof window !== "undefined"
      ? localStorage.getItem("kiosk_stripe_reader_id")
      : null;

  // Get stateId from URL - this is the canonical fixture ID used for S3 uploads
  const stateId = getStateIdFromUrl();

  // Extract consumer name from trace context for logging
  const consumerName = extractConsumerName(trace);

  // Detect OCR outcome for test video file selection and extract captured data
  const ocrOutcome = detectOcrOutcome(statePath, trace);

  return {
    traceId: trace.traceId,
    generatedAt: new Date().toISOString(),
    initialState: trace.initialState,
    finalState,
    eventCount: machineEvents.length,
    domEventCount,
    duration,
    statePath,
    statePathFingerprint,
    terminalId,
    // Fixture ID for S3 test replay - uses stateId from URL (same as insurance actor and pricing actions)
    fixtureId: stateId || trace.traceId,
    consumerName,
    ocrOutcome,
  };
}

// ============================================================================
// Test Name Generation
// ============================================================================

/**
 * Generate a descriptive test name from the trace flow
 */
export function generateTestName(trace: MachineTrace): string {
  const machineEvents = trace.events.filter(
    (e) => e.inspectionType === "@xstate.event",
  );
  const eventTypes = machineEvents
    .map((e) => e.machineEvent?.type)
    .filter(Boolean)
    .slice(0, 5); // First 5 events

  const flowHint = eventTypes.length > 0 ? eventTypes.join(" → ") : "flow";
  const timestamp = new Date().toISOString().slice(0, 10);

  return `Generated Test: ${flowHint} (${timestamp})`;
}
